<template lang="pug">
.announcement-block-editor
  .editor-header
    h3 {{ isCreating ? 'New Announcement' : 'Announcement' }}

  .editor-form
    el-form(:model="formData" label-position="top" :rules="rules" ref="formRef")
      el-form-item(label="Text" prop="text")
        el-input(
          v-model="formData.text"
          placeholder="Enter announcement text"
          size="default"
        )

      el-form-item(label="Text Color" prop="textColor")
        custom-color-picker(
          v-model="formData.textColor"
          placeholder="#000000"
          :predefine-colors="predefineColors"
        )

      el-form-item(label="Background Color" prop="textBackgroundColor")
        custom-color-picker(
          v-model="formData.textBackgroundColor"
          placeholder="#ffffff"
          :predefine-colors="predefineColors"
        )

      .editor-actions
        el-button(
          @click="handleCancel"
          size="default"
        ) Cancel
        el-button(
          type="primary"
          @click="handleSave"
          size="default"
          :loading="saving"
        ) Save
</template>

<script>
import {
  defineComponent, ref, reactive,
} from 'vue';
import { ElMessage } from 'element-plus';
import useStaticPageUtils from '../shared/index.ts';
import CustomColorPicker from '../../../../../components/CustomColorPicker.vue';

export default defineComponent({
  name: 'AnnouncementBlock',
  components: {
    CustomColorPicker,
  },
  props: {
    block: {
      type: Object,
      default: null,
    },
    isCreating: {
      type: Boolean,
      default: false,
    },
    pageId: {
      type: String,
      required: true,
    },
    staticPageData: {
      type: Object,
      default: null,
    },
  },
  emits: ['save', 'cancel'],
  setup(props, { emit }) {
    const { createOrUpdateAnnouncementBlock } = useStaticPageUtils();
    const formRef = ref(null);
    const saving = ref(false);

    // Predefined colors for the color picker
    const predefineColors = [
      '#000000',
      '#FFFFFF',
      '#1EBEAF',
      '#67C23A',
      '#E6A23C',
      '#F56C6C',
      '#909399',
      '#234762',
      '#4CAF50',
      '#2196F3',
      '#FF9800',
      '#9C27B0',
    ];

    // Convert rgba to hex
    const rgbaToHex = (rgba) => {
      // Handle hex format already
      if (!rgba || rgba.startsWith('#')) {
        return rgba || '#000000';
      }

      // Handle rgba format
      const rgbaMatch = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d*\.?\d+))?\)/);
      if (rgbaMatch) {
        const r = parseInt(rgbaMatch[1], 10);
        const g = parseInt(rgbaMatch[2], 10);
        const b = parseInt(rgbaMatch[3], 10);

        // Convert RGB to hex
        const toHex = (n) => {
          const hex = n.toString(16);
          return hex.length === 1 ? `0${hex}` : hex;
        };
        return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
      }

      return rgba;
    };

    // Form data
    const formData = reactive({
      text: props.block?.details?.text || '',
      textColor: rgbaToHex(props.block?.details?.textColor) || '#000000',
      textBackgroundColor: rgbaToHex(props.block?.details?.textBackgroundColor) || '#ffffff',
    });

    // Form validation rules
    const rules = {
      text: [
        { required: true, message: 'Please enter announcement text', trigger: 'blur' },
      ],
      textColor: [
        { required: true, message: 'Please select a text color', trigger: 'change' },
      ],
      textBackgroundColor: [
        { required: true, message: 'Please select a background color', trigger: 'change' },
      ],
    };

    // Handle save
    async function handleSave() {
      if (!formRef.value) return;

      try {
        await formRef.value.validate();
        saving.value = true;

        const input = {
          text: formData.text,
          textColor: rgbaToHex(formData.textColor),
          textBackgroundColor: rgbaToHex(formData.textBackgroundColor),
          sortOrder: props.isCreating ? null : (props.block?.sortOrder || 0),
          parentId: props.pageId,
          id: props.isCreating ? null : props.block?.id,
        };

        const result = await createOrUpdateAnnouncementBlock(input, props.staticPageData);

        ElMessage.success(props.isCreating ? 'Announcement block created successfully' : 'Announcement block updated successfully');
        emit('save', result);
      } catch (error) {
        console.error('Error saving announcement block:', error);
        ElMessage.error('Failed to save announcement block');
      } finally {
        saving.value = false;
      }
    }

    // Handle cancel
    function handleCancel() {
      emit('cancel');
    }

    return {
      formRef,
      formData,
      rules,
      saving,
      predefineColors,
      handleSave,
      handleCancel,
    };
  },
});
</script>

<style scoped>
.announcement-block-editor {
  width: 100%;
}

.editor-form {
  max-width: 500px;
  margin-top: 20px;
}

.color-picker-container {
  display: flex;
  align-items: center;
  gap: 12px;
}

.color-input {
  max-width: 150px;
}

/* Removed color preview */

:deep(.el-color-picker__trigger) {
  width: 40px;
  height: 40px;
  padding: 2px;
}

:deep(.el-color-picker__color) {
  border-radius: 4px;
}

/* Custom color picker layout */
:deep(.el-color-dropdown__main-wrapper) {
  display: flex !important;
  flex-direction: row !important;
}

:deep(.el-color-dropdown__btns) {
  margin-top: 8px !important;
}

:deep(.el-color-hue-slider) {
  order: -1 !important;
  margin-right: 12px !important;
  margin-left: 0 !important;
  width: 12px !important;
}

:deep(.el-color-hue-slider__bar) {
  width: 12px !important;
}

:deep(.el-color-svpanel) {
  order: 2 !important;
  margin-left: 0 !important;
}

.editor-actions {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #ebeef5;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}
</style>
